/* eslint-disable arrow-body-style */
import { useCallback, useState } from 'react';
import { Promise } from 'bluebird';
import { get } from 'underscore';
import { CSMLAssetSource } from '../../model';
import { getTaskPoller } from '../task-poller';

export const useEditImageService = ({
  repository,
  logger,
  pollInterval = 3000,
  maxRetryCount = 3, // max retry count for polling task status
}) => {
  const [imagesWithFilter, setImagesWithFilter] = useState([]);
  const [imagesWithFilterAndBlur, setImagesWithFilterAndBlur] = useState([]);

  const getImagesWithFilter = useCallback((imageUrl) => {
    return repository.generateImageWithSmartEffect({
      imageUrl,
      smartEffects: [{
        smartEffectName: 'design_ideas',
        smartEffectOptions: {},
      }],
    }).then((res = []) => {
      const filterRes = res.map(filter => ({
        filter: filter.Effect,
        url: filter.Resource,
      }));
      filterRes.unshift({
        url: imageUrl,
        filter: 'None',
      });
      setImagesWithFilter(filterRes);
      logger.logTrace('edit image services: get image filters successfully');
      return filterRes;
    }).catch((err) => {
      logger.logError('edit image services: get image filters failed', err);
      return [];
    });
  }, [logger, repository]);

  const getImagesWithFilterAndBlurImp = useCallback((imageUrl, blurLevel) => {
    return repository.generateImageWithSmartEffect({
      imageUrl,
      smartEffects: [{
        smartEffectName: 'bg_blur',
        smartEffectOptions: blurLevel ? {
          ManualBgBlurLevel: blurLevel,
        } : {},
      },
      {
        smartEffectName: 'design_ideas',
        smartEffectOptions: {},
      },
      ],
    }).then((assets = []) => {
      return assets;
    });
  }, [repository]);

  const getOriginalImageWithBlur = useCallback((imageUrl, blurLevel) => {
    return repository.generateImageWithSmartEffect({
      imageUrl,
      smartEffects: [{
        smartEffectName: 'bg_blur',
        smartEffectOptions: blurLevel ? {
          ManualBgBlurLevel: blurLevel,
        } : {},
      }],
    }).then((assets = []) => {
      return assets;
    });
  }, [repository]);

  const getImagesWithFilterAndBlur = useCallback((imageUrl, blurLevel) => {
    return Promise.all([getOriginalImageWithBlur(imageUrl, blurLevel), getImagesWithFilterAndBlurImp(imageUrl, blurLevel)]).then(([originalImageWithBlurRes, imagesWithFilterAndBlurRes]) => {
      const res = [...originalImageWithBlurRes, ...imagesWithFilterAndBlurRes];
      const imagesWithFilterAndBlurInner = res.map((asset) => {
        const filter = get(asset, 'Effect', '');
        const url = get(asset, 'Resource', '');
        return {
          filter,
          blur: blurLevel,
          url,
        };
      });
      setImagesWithFilterAndBlur(imagesWithFilterAndBlurInner);
      logger.logTrace('edit image services: get image filters and blur successfully');
      return imagesWithFilterAndBlurInner;
    });
  }, [getImagesWithFilterAndBlurImp, getOriginalImageWithBlur, logger]);

  const removeImageBackground = useCallback((imageUrl) => {
    return repository.removeImageBackground(imageUrl);
  }, [repository]);

  const generateImageBackground = useCallback(({
    prompt,
    imageUrl,
    maskSetsBase64,
    selectedMasks,
    outputImageWidth,
    outputImageHeight,
  }) => {
    return repository.generateImageBackground({
      prompt,
      imageUrl,
      maskSetsBase64,
      selectedMasks,
      outputImageWidth,
      outputImageHeight,
    }).then((res) => {
      const { imageUrl: newImageUrl } = res || {};
      logger.logTrace('edit image services: generate image background successfully', { generatedUrl: newImageUrl, prompt });
      return res;
    });
  }, [logger, repository]);

  /* istanbul ignore next */
  const generateImageBackgrounds = useCallback(({
    prompt,
    imageUrl,
    maskSetsBase64,
    selectedMasks,
    outputImageWidth,
    outputImageHeight,
  }) => {
    if (!repository) {
      return Promise.reject(new Error());
    }

    const generateImageBackgroundsByAIPromise = () => new Promise((resolve, reject) => {
      // start generating task
      repository.generateImageBackgrounds({
        prompt, imageUrl, maskSetsBase64, selectedMasks, outputImageWidth, outputImageHeight,
      }).then((res) => {
        const { taskId } = res || {};

        if (!taskId) {
          resolve({
            prompt, images: [],
          });
          return;
        }

        // poll task status
        let retryCount = 0;
        const pollStatus = () => repository.getGeneratedBackgroundImagesTaskStatus(taskId).then((taskStatus) => {
          // taskId, status, progress, images, isTaskDone,
          const { isTaskDone, ...rest } = taskStatus || {};

          // reset retry count if there is successfull poll
          retryCount = 0;

          if (isTaskDone) {
            resolve({ ...rest, prompt });
          }

          // stop polling if it's done
          return { isFinished: isTaskDone };
        }).catch((err) => {
          retryCount += 1;
          if (err.shouldRetry === false || retryCount >= maxRetryCount) { // got continuous errors
            reject(err);
            // stop polling if failed maxRetryCount times continuously
            return { isFinished: true };
          }

          // ignore errors and retry
          logger.logError(`failed to get background generation task status, retryCount: ${retryCount}, taskId: ${taskId}, prompt: ${prompt}, error: ${err.uxMessage}`);
          return { isFinished: false };
        });

        // start polling task status
        const taskPoller = getTaskPoller({ task: pollStatus, interval: pollInterval });
        taskPoller.start();
      }).catch((err) => {
        reject(err);
      });
    });

    const ret = generateImageBackgroundsByAIPromise();

    if (logger.monitorPromise) {
      return logger.monitorPromise({ promise: ret, msg: `generateImageBackgrounds, prompt: ${prompt}`, api: 'GenerateImageBackgrounds' });
    }
    return ret;
  }, [logger, maxRetryCount, pollInterval, repository]);

  const getImageSegmentations = useCallback((imageUrl) => {
    return repository.getImageSegmentations(imageUrl).then((res) => {
      logger.logTrace('edit image services: get image segmentations successfully', { originalUrl: imageUrl });
      return res;
    });
  }, [logger, repository]);

  // Save function for background change generated image
  const saveImageCopy = useCallback(({
    image,
    folderId = null,
  }) => {
    const imageUrl = image.url;
    return repository.saveImageCopy({
      imageUrl,
      folderId,
      sourceType: CSMLAssetSource.BackgroundChangeGenerated,
    }).then((res) => {
      logger.logTrace('edit image services: image is saved as copy', { imageUrl, folderId });
      return res;
    });
  }, [logger, repository]);

  const saveImage = useCallback(({
    id,
    type,
    url,
  }) => {
    return repository.saveImage({
      imageUrl: url,
      imageId: id,
      type,
    }).then((res) => {
      logger.logTrace('edit image services: image is saved directly', { url, id });
      return res;
    });
  }, [logger, repository]);

  const cropImage = useCallback(({ imageId, cropSettings, placementSettings }) => {
    return repository.cropImage({ imageId, cropSettings, placementSettings }).then(() => {
      logger.logTrace('edit image services: image is cropping', { imageId });
    });
  }, [logger, repository]);

  const getImageById = useCallback(({ imageId, isImageFittingEnabled }) => {
    return repository.getImageById({ imageId, isImageFittingEnabled }).then((res) => {
      logger.logTrace('edit image services: get image by id', { imageId });
      return res;
    });
  }, [logger, repository]);

  const uploadImageToFolder = useCallback(({
    imageData,
    folderId = null,
  }) => {
    return repository.uploadImageToFolder({
      imageData,
      folderId,
    });
  }, [repository]);

  const saveImageWithBaseId = useCallback(({
    image,
    baseId,
    folderId = null,
  }) => {
    return repository.saveImageWithBaseId({ image, baseId, folderId });
  }, [repository]);

  const uploadStockImage = useCallback(({ stockImageId, folderId }) => {
    return repository.uploadStockImageToFolder({ stockImageId, folderId }).tap(() => {
      logger.logTrace('edit image services: stock image is uploaded to folder', { stockImageId, folderId });
    });
  }, [logger, repository]);

  const moveImageToFolder = useCallback(({
    image,
    destFolderId,
  }) => {
    return repository.moveImageToFolder({
      image,
      destFolderId,
    });
  }, [repository]);

  const imageFit = useCallback(({
    imageUrl,
    placements,
    skipOutpainting,
  }) => {
    return repository.imageFit({
      imageUrl,
      placements,
      skipOutpainting,
    });
  }, [repository]);

  const addImageWithBaseIdToFolder = useCallback(({
    imageSrc, baseId, folderId, metaData,
  }) => {
    return repository.addImageWithBaseIdToFolder({
      imageSrc,
      baseId,
      folderId,
      metaData,
    });
  }, [repository]);

  return {
    getImagesWithFilter,
    getImagesWithFilterAndBlur,
    imagesWithFilter,
    imagesWithFilterAndBlur,
    removeImageBackground,
    generateImageBackground,
    generateImageBackgrounds,
    getImageSegmentations,
    saveImage,
    saveImageCopy,
    cropImage,
    getImageById,
    uploadImageToFolder,
    saveImageWithBaseId,
    uploadStockImage,
    moveImageToFolder,
    imageFit,
    addImageWithBaseIdToFolder,
  };
};
